import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Switcher from 'components/ui/switcher/Switcher';
import BlazeInputDropdown, { BlazeDropdownItem } from 'components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import acApi from 'axios/ac/acApi';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { AccountSettingsDto, AccountSettingsPatchDto, IdleStopNotify } from 'types/rest/accountSettingsTypes';

const INACTIVITY_THRESHOLD_OPTIONS: BlazeDropdownItem[] = [
  { id: 7, name: '7 days' },
  { id: 14, name: '14 days' },
  { id: 30, name: '30 days' },
  { id: 60, name: '60 days' },
  { id: 90, name: '90 days' },
];

const NOTIFY_OPTIONS: BlazeDropdownItem[] = [
  { id: 'NONE', name: 'None' },
  { id: 'OWNER', name: 'Owner only' },
  { id: 'ADMINS', name: 'Admins only' },
  { id: 'OWNER_AND_ADMINS', name: 'Owner and admins' },
];

const DEFAULT_THRESHOLD = INACTIVITY_THRESHOLD_OPTIONS[1]; // 14 days
const DEFAULT_NOTIFY = NOTIFY_OPTIONS[0]; // NONE

const AutoShutdownSettings: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState<BlazeDropdownItem>(DEFAULT_THRESHOLD);
  const [notify, setNotify] = useState<BlazeDropdownItem>(DEFAULT_NOTIFY);
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [savedThreshold, setSavedThreshold] = useState<BlazeDropdownItem>(DEFAULT_THRESHOLD);
  const [savedNotify, setSavedNotify] = useState<BlazeDropdownItem>(DEFAULT_NOTIFY);

  const isDirty = enabled !== savedEnabled || threshold.id !== savedThreshold.id || notify.id !== savedNotify.id;

  useEffect(() => {
    acApi.accountSettings
      .getSettings()
      .then(response => {
        const settings: AccountSettingsDto = response.data.result;
        const loadedThreshold =
          INACTIVITY_THRESHOLD_OPTIONS.find(o => o.id === settings.idleStopDays) ?? DEFAULT_THRESHOLD;
        const loadedNotify = NOTIFY_OPTIONS.find(o => o.id === settings.idleStopNotify) ?? DEFAULT_NOTIFY;

        setEnabled(settings.idleStopEnabled);
        setThreshold(loadedThreshold);
        setNotify(loadedNotify);
        setSavedEnabled(settings.idleStopEnabled);
        setSavedThreshold(loadedThreshold);
        setSavedNotify(loadedNotify);
      })
      .catch(() => {
        dispatch(showToastNotification(ToastType.Danger, '', t('admin:autoShutdown.loadError')));
      })
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = () => {
    setEnabled(savedEnabled);
    setThreshold(savedThreshold);
    setNotify(savedNotify);
  };

  const handleSave = () => {
    setSaving(true);
    const patch: AccountSettingsPatchDto = {
      idleStopEnabled: enabled,
      idleStopDays: threshold.id as number,
      idleStopNotify: notify.id as IdleStopNotify,
    };
    acApi.accountSettings
      .patchSettings(patch)
      .then(() => {
        setSavedEnabled(enabled);
        setSavedThreshold(threshold);
        setSavedNotify(notify);
        dispatch(showToastNotification(ToastType.Success, '', t('admin:autoShutdown.saveSuccess')));
      })
      .catch(() => {
        dispatch(showToastNotification(ToastType.Danger, '', t('admin:autoShutdown.saveError')));
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <section className="admin-page">
        <h1>{t('admin:pages.autoShutdownSettings.title')}</h1>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <h1>{t('admin:pages.autoShutdownSettings.title')}</h1>
      <p className="admin-page-subtitle">{t('admin:pages.autoShutdownSettings.subtitle')}</p>

      <div className="auto-shutdown-card">
        <div className="auto-shutdown-card-header">
          <span className="auto-shutdown-card-title">{t('admin:autoShutdown.cardTitle')}</span>
          <span className="auto-shutdown-account-badge">{t('admin:autoShutdown.accountLevel')}</span>
        </div>
        <p className="auto-shutdown-card-description">{t('admin:autoShutdown.cardDescription')}</p>

        <Switcher
          checked={enabled}
          onChange={() => setEnabled(prev => !prev)}
          size="lg"
          label={t('admin:autoShutdown.enableLabel')}
        />

        <hr className="auto-shutdown-divider" />

        <div className="auto-shutdown-field-row">
          <span className="auto-shutdown-field-label">{t('admin:autoShutdown.thresholdLabel')}</span>
          <BlazeInputDropdown
            id="qa-auto-shutdown-threshold"
            items={INACTIVITY_THRESHOLD_OPTIONS}
            selected={threshold}
            onClick={setThreshold}
            isSearchable={false}
            disabled={!enabled}
            dropdownWidth={200}
          />
        </div>
        <p className="auto-shutdown-field-hint">{t('admin:autoShutdown.thresholdHint')}</p>

        <div className="auto-shutdown-field-row">
          <span className="auto-shutdown-field-label">{t('admin:autoShutdown.notifyLabel')}</span>
          <BlazeInputDropdown
            id="qa-auto-shutdown-notify"
            items={NOTIFY_OPTIONS}
            selected={notify}
            onClick={setNotify}
            isSearchable={false}
            disabled={!enabled}
            dropdownWidth={220}
          />
        </div>

        <div className="alert alert-info auto-shutdown-info-box" role="alert">
          <i className="icon icon-information alert-icon mr-2" />
          {t('admin:autoShutdown.infoText')}
        </div>
      </div>

      <div className="auto-shutdown-footer">
        <button className="btn btn-outline-secondary mr-2" type="button" onClick={handleCancel} disabled={saving}>
          {t('common:cancel')}
        </button>
        <button className="btn btn-primary" type="button" onClick={handleSave} disabled={saving || !isDirty}>
          {t('admin:autoShutdown.saveChanges')}
        </button>
      </div>
    </section>
  );
};

export default AutoShutdownSettings;
